import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { getArticleBySlug, articles } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : null;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 pb-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = getCategoryBySlug(article.categorySlug);
  const categoryName = category ? category.name : article.categorySlug;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName, href: `/category/${article.categorySlug}` },
    ...(article.subcategorySlug ? [{ label: article.tags[0], href: `/category/${article.subcategorySlug}` }] : []),
    { label: article.title },
  ];

  const relatedArticles = articles
    .filter(a => a.id !== article.id && (a.categorySlug === article.categorySlug || a.subcategorySlug === article.subcategorySlug))
    .slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.coverImage,
    "datePublished": article.date,
    "author": {
      "@type": "Organization",
      "name": "亚洲精品一区二区"
    },
    "publisher": {
      "@type": "Organization",
      "name": "亚洲精品一区二区"
    }
  };

  return (
    <>
      <SchemaOrg schema={articleSchema} />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-20 pb-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-xs bg-accent-100 text-accent-700 px-2.5 py-1 rounded-full whitespace-nowrap">
                {article.tags[0]}
              </span>
              <span className="text-xs text-foreground-500">{article.date}</span>
              <span className="text-xs text-foreground-500">{article.readTime} 分钟阅读</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-foreground-600 text-sm leading-relaxed">
              {article.excerpt}
            </p>
          </header>

          <div className="mb-10 rounded-lg overflow-hidden">
            <div className="w-full h-[300px] md:h-[450px]">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div
            className="prose prose-sm max-w-none text-foreground-800 leading-relaxed
              [&>h2]:font-heading [&>h2]:text-xl [&>h2]:font-bold [&>h2]:text-foreground-900 [&>h2]:mt-8 [&>h2]:mb-4
              [&>h3]:font-heading [&>h3]:text-lg [&>h3]:font-semibold [&>h3]:text-foreground-900 [&>h3]:mt-6 [&>h3]:mb-3
              [&>p]:mb-4 [&>p]:leading-relaxed
              [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:mb-4
              [&>ol]:list-decimal [&>ol]:pl-5 [&>ol]:mb-4"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <span key={tag} className="text-xs bg-background-100 text-foreground-600 px-3 py-1.5 rounded-full whitespace-nowrap">
                #{tag}
              </span>
            ))}
          </div>

          {relatedArticles.length > 0 && (
            <section className="mt-14 pt-10 border-t border-background-200/70">
              <h2 className="font-heading font-bold text-xl text-foreground-900 mb-6">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:bg-background-200/50"
                  >
                    <div className="h-40 overflow-hidden">
                      <div className="w-full h-full">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                      <span className="text-xs text-foreground-500">{ra.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}